/**
 * EnemyCharacter — created per-battle from an EnemyDef.
 * Extends GameCharacter for shared combat stats.
 *
 * Enemy level scales stats above the base definition.
 * Level 1 = base stats. Each level above 1 adds per-level bonuses.
 */
import { GameCharacter } from "./GameCharacter";

export type EnemyTier = "normal" | "elite" | "boss";

export interface EnemyDef {
  name: string;
  maxHp: number;
  attack: number;
  armor: number;
  goldReward: number;
  tier: EnemyTier;
}

/** Per-level stat growth above level 1. */
const LEVEL_SCALING: Record<EnemyTier, { hp: number; attack: number; armor: number; gold: number }> = {
  normal: { hp: 2, attack: 1, armor: 0, gold: 2 },
  elite:  { hp: 4, attack: 1, armor: 1, gold: 3 },
  boss:   { hp: 6, attack: 1, armor: 1, gold: 5 },
};

export class EnemyCharacter extends GameCharacter {
  readonly goldReward: number;
  readonly tier: EnemyTier;
  readonly level: number;

  constructor(def: EnemyDef, level: number = 1) {
    const bonus = Math.max(0, level - 1);
    const scale = LEVEL_SCALING[def.tier];
    super(
      def.name,
      def.maxHp + bonus * scale.hp,
      def.attack + bonus * scale.attack,
      def.armor + bonus * scale.armor,
    );
    this.goldReward = def.goldReward + bonus * scale.gold;
    this.tier = def.tier;
    this.level = level;
  }
}
